import React, {useCallback, useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Modal} from "../../components";
import {AiFillWindows} from "react-icons/ai";
import {ThemeContext} from "../../contexts/ThemeContext";
import OneDrawLogo from "../../assets/img/logos/one_draw_logo.png";
import OneDrawLogoWhite from "../../assets/img/logos/one_draw_logo_white.png";
import AuthGradient from "../../assets/img/background-gradients/bg-gradient.png";
import AuthPattern from "../../assets/img/background-gradients/bg-pattern.png";
import getMsalInstance, {loginRequest} from "../../helpers/getMsalInstance";
import {Button} from "antd";
import {useViewerStore} from "../../store/store";
import {logger} from "../../services/loggingService";
import {useIsAuthenticated} from "@azure/msal-react";
import type {PublicClientApplication} from "@azure/msal-browser";

interface UserLoginSuccessEvent extends CustomEvent<{ redirect: boolean; validated: boolean }> {
}

const LoginPage: React.FC = () => {
    const msalInstance: PublicClientApplication = getMsalInstance();
    const {theme} = useContext(ThemeContext);
    const navigate = useNavigate();

    const [showModal, setShowModal] = useState<boolean>(false);
    const [isLoginProcessing, setIsLoginProcessing] = useState<boolean>(false);
    const isAzureAuthenticated = useIsAuthenticated();

    const closeModal = useCallback(() => {
        msalInstance.logoutRedirect();
        setShowModal(false);
    }, [msalInstance]);

    const handleUserLoginSuccess = useCallback(
        (event: Event) => {
            const customEvent = event as UserLoginSuccessEvent;
            if (customEvent.detail?.redirect && customEvent.detail?.validated) {
                useViewerStore.setState({isUserRedirectedToSiteAfterLogin: true});
                navigate("/sites");
            }
        },
        [navigate]
    );

    const handleUserHasNoEntitlement = useCallback(() => {
        setShowModal(true);
        sessionStorage.clear();
        localStorage.clear();
    }, []);

    useEffect(() => {
        window.addEventListener("userLoginSuccess", handleUserLoginSuccess);
        window.addEventListener("userHasNoEntitlement", handleUserHasNoEntitlement);

        return () => {
            window.removeEventListener("userLoginSuccess", handleUserLoginSuccess);
            window.removeEventListener("userHasNoEntitlement", handleUserHasNoEntitlement);
        };
    }, [handleUserLoginSuccess, handleUserHasNoEntitlement]);

    const handleSignIn = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        sessionStorage.setItem("loginMessage", "Login Successful, Redirecting...");
        setIsLoginProcessing(true);

        try {
            msalInstance.loginRedirect(loginRequest);
        } catch (error) {
            logger.error(error);
            setIsLoginProcessing(false);
        }
    };

    const year = new Date().getFullYear();

    return (
        <div className="app__layout auth">
            <section className="auth">
                <div className="auth__header">
                    <div className="auth__header--logo">
                        {theme === "dark" ? (
                            <img src={OneDrawLogoWhite} alt="OneDraw"/>
                        ) : (
                            <img src={OneDrawLogo} alt="OneDraw"/>
                        )}
                    </div>
                </div>

                <div className="auth__bg">
                    <img src={AuthGradient} alt="auth_gradient"/>
                </div>
                <div className="auth__bg bg-pattern">
                    <img src={AuthPattern} alt="auth_pattern"/>
                </div>

                <div className="auth__content">
                    <h1 className="auth__title">Welcome to OneDraw</h1>
                    <p className="auth__text">Login to OneDraw securely using your Microsoft account</p>

                    {isLoginProcessing ? (
                        <button className="auth__btn" disabled>
                            <AiFillWindows/>
                            Redirecting...
                        </button>
                    ) : (
                        <button
                            className="auth__btn"
                            disabled={isAzureAuthenticated}
                            onClick={handleSignIn}
                        >
                            <AiFillWindows/>
                            {sessionStorage.getItem("loginMessage") ??
                                "Login with Microsoft"}
                        </button>
                    )}
                </div>

                <div className="auth__footer">
                    <p className="auth__footer--copy">
                        © Trinity Solar, Inc. {year}. All Rights Reserved.
                    </p>
                </div>
            </section>

            {showModal && (
                <Modal title="Unauthorized Access!" messageType="error" close={closeModal}>
                    This account does not have access to oneDRAW. Please logout and try with a
                    different account or contact your administrator for permission to access
                    the application.
                    <br/>
                    <br/>
                    When you close this message, you will be asked to log out of your
                    account. Kindly sign out and try again.
                    <Button
                        type="primary"
                        onClick={closeModal}
                        style={{marginTop: "20px", background: "indianred"}}
                    >
                        Close and Logout
                    </Button>
                </Modal>
            )}
        </div>
    );
};

export default LoginPage;